/**
 * CA 登录统一调度器
 *
 * 通过 channelMaterial.getCaType() 判定当前渠道使用的 CA 类型，
 * 并把调用分发到对应的实现（如 BJCA）。
 *
 * 当前已实现：
 *   - BJCA: 基于 XTXASynObject (WebSocket 模式)
 *
 * 后续新增 CA（如 HBCA、HDDC 等）只需：
 *   1. 在本目录新增 xxxLogin.js，实现同样的接口
 *   2. 在下面 IMPL_MAP 中登记 caType -> impl
 *   3. 在 channelMaterial.js 给相应渠道配置 caType
 */

import channelMaterial from '@/utils/channelMaterial'
import { bjcaLogin } from './bjcaLogin'

/**
 * CA 实现统一接口
 * @typedef {Object} CaLoginImpl
 * @property {string} caType          渠道标识，需与 channelMaterial 的 caType 字段一致
 * @property {() => Promise<any>} ensureReady  确保底层控件/连接已就绪
 * @property {() => Promise<Array<{name:string, certid:string}>>} getCertList
 * @property {(certId:string) => Promise<string>} getUserCert  导出证书原文
 * @property {(certId:string, plain:string) => Promise<string>} signData  私钥签名
 * @property {(certId:string) => Promise<boolean>} isLoggedIn
 */

/** @type {Record<string, CaLoginImpl>} */
const IMPL_MAP = {
  BJCA: bjcaLogin
  // HBCA: hebcaLogin,  // 后续接入
}

/**
 * 获取当前渠道对应的 CA 实现，找不到时回退到 BJCA
 * @returns {CaLoginImpl}
 */
export function getCaImpl() {
  const caType = (channelMaterial.getCaType() || 'BJCA').toUpperCase()
  return IMPL_MAP[caType] || IMPL_MAP.BJCA
}

/**
 * 获取当前渠道使用的 CA 类型字符串
 */
export function getCaType() {
  return (channelMaterial.getCaType() || 'BJCA').toUpperCase()
}

/**
 * 是否已实现该 caType（用于 UI 上决定是否展示 CA 登录 Tab）
 */
export function isCaTypeSupported(caType) {
  if (!caType) return false
  return Boolean(IMPL_MAP[String(caType).toUpperCase()])
}

/**
 * 便捷转发：以下方法都委托给当前 caType 对应的实现
 */
export const caLoginHelper = {
  getCaType,
  getCaImpl,
  isCaTypeSupported,
  ensureReady: (...args) => getCaImpl().ensureReady(...args),
  getCertList: (...args) => getCaImpl().getCertList(...args),
  getUserCert: (...args) => getCaImpl().getUserCert(...args),
  signData: (...args) => getCaImpl().signData(...args),
  isLoggedIn: (...args) => getCaImpl().isLoggedIn(...args)
}

export default caLoginHelper
